import React, { useState, useEffect } from 'react';
import { getWhitelistedIps, addWhitelistedIp, removeWhitelistedIp, generateApiCredentials } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { Plus, Trash2, Key, Copy } from 'lucide-react';

const Developer = () => {
  const { user } = useAuth();
  const [ips, setIps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddIp, setShowAddIp] = useState(false);
  const [ipForm, setIpForm] = useState({ ipAddress: '', description: '' });
  const [credentials, setCredentials] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { loadIps(); }, []);

  const loadIps = async () => {
    try { 
      const res = await getWhitelistedIps(); 
      
      // The API returns an IP array directly
      let ipsData = [];
      if (Array.isArray(res.data)) {
        ipsData = res.data.map(ip => ({
          id: ip.id,
          ipAddress: ip.ipAddress,
          description: ip.description || 'No description',
          isActive: ip.isActive,
          createdAt: ip.createdAt
        }));
      }
      
      setIps(ipsData); 
    } 
    catch (err) { 
      console.error('Failed to load IPs:', err); 
      showErrorToast('Failed to load whitelisted IPs');
      setIps([]); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleAddIp = async (e) => {
    e.preventDefault();
    try {
      const addRes = await addWhitelistedIp(ipForm);
      showSuccessToast('IP added successfully');
      setShowAddIp(false);
      setIpForm({ ipAddress: '', description: '' });
      
      // Wait a moment before reloading to ensure backend has processed the addition
      setTimeout(() => {
        loadIps();
      }, 500);
    } catch (err) { 
      console.error('Add IP Error:', err); // Debug: Log error
      showErrorToast(err.response?.data?.message || 'Failed to add IP'); 
    }
  };

  const handleRemoveIp = async (ipId) => {
    if (!window.confirm('Remove this IP?')) return;
    try {
      const removeRes = await removeWhitelistedIp(ipId);
      showSuccessToast('IP removed successfully');
      loadIps();
    } catch (err) { 
      console.error('Remove IP Error:', err); // Debug: Log error
      showErrorToast(err.response?.data?.message || 'Failed to remove IP'); 
    }
  };

  const handleGenerateCredentials = async () => {
    if (!window.confirm('Generate new API credentials? This action cannot be undone.')) return;
    setGenerating(true);
    try {
      const res = await generateApiCredentials();
      setCredentials(res.data);
      showSuccessToast('Credentials generated');
    } catch (err) { 
      showErrorToast(err.response?.data?.message || 'Failed'); 
    } finally { 
      setGenerating(false); 
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSuccessToast('Copied to clipboard');
  };

  if (loading) return <div className="flex justify-center p-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Developer Options</h1>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>API Credentials</CardTitle>
          {!credentials && <Button onClick={handleGenerateCredentials} disabled={generating}><Key className="h-4 w-4 mr-2" /> Generate Credentials</Button>}
        </CardHeader>
        <CardContent>
          {credentials ? (
            <div className="space-y-4 p-4 bg-amber-50 rounded border border-amber-200">
              <p className="text-amber-800 text-sm font-medium">⚠️ Save these credentials now. They won't be shown again!</p>
              <div>
                <Label>API Key</Label>
                <div className="flex gap-2">
                  <Input value={credentials.apiKey} readOnly className="font-mono" />
                  <Button variant="outline" onClick={() => copyToClipboard(credentials.apiKey)}><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
              <div>
                <Label>API Token</Label>
                <div className="flex gap-2">
                  <Input value={credentials.apiToken} readOnly className="font-mono" />
                  <Button variant="outline" onClick={() => copyToClipboard(credentials.apiToken)}><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">Click "Generate Credentials" to create your API key and token.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Whitelisted IPs</CardTitle>
          <Button onClick={() => setShowAddIp(true)}><Plus className="h-4 w-4 mr-2" /> Add IP</Button>
        </CardHeader>
        <CardContent>
          {ips.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No IPs whitelisted</p>
          ) : (
            <div className="space-y-2">
              {ips.map(ip => (
                <div key={ip.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-mono text-sm">{ip.ipAddress}</p>
                      <span className={`px-2 py-1 text-xs rounded ${ip.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {ip.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {ip.description && <p className="text-sm text-gray-500 mt-1">{ip.description}</p>}
                    {ip.createdAt && <p className="text-xs text-gray-400 mt-1">Added: {new Date(ip.createdAt).toLocaleDateString()}</p>}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleRemoveIp(ip.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAddIp} onOpenChange={setShowAddIp}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Whitelisted IP</DialogTitle></DialogHeader>
          <form onSubmit={handleAddIp} className="space-y-4">
            <div><Label>IP Address *</Label><Input value={ipForm.ipAddress} onChange={(e) => setIpForm({...ipForm, ipAddress: e.target.value})} placeholder="192.168.1.1" required /></div>
            <div><Label>Description</Label><Input value={ipForm.description} onChange={(e) => setIpForm({...ipForm, description: e.target.value})} placeholder="Office server" /></div>
            <Button type="submit" className="w-full">Add IP</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Developer;
